import { Api } from '../../api'
import { UserServiceI, PurchasesParams, SignInParams, User as UserI, UserEvent } from '../../../index.d'
import { HOST } from '../constants'
import { User, DEFAULT_USER } from '../entities/user'

export class UserService implements UserServiceI {
  /**
   * Get current user data
   * @return {Promise} Server response with user data
   */
  async getUser(): Promise<User> {
    try {
      const data = await Api.get<UserI>('/api/user/me').then(({ data }) => data)
      const user = new User(data)
      return user
    } catch (error) {
      return new User(DEFAULT_USER)
    }
  }

  /**
   * Create user instance
   * @param {UserI} data data of user
   */
  createUser(data: UserI): User {
    return new User(data)
  }

  /**
   * Get current user purchases
   * @param  {Number} params.resourceId     Resource ID
   * @param  {String} params.resourceType   Resource type
   * @return {Promise}                      Server response with user purchases
   */
  async purchases({ resourceId, resourceType }: PurchasesParams): Promise<unknown> {
    if (resourceId && typeof resourceId !== 'number') {
      throw new Error('resourceId must be a number')
    }
    if (resourceType && typeof resourceType !== 'string') {
      throw new Error('resourceType must be a string')
    }
    const params = []
    if (resourceId) params.push(`resource_id=${encodeURIComponent(resourceId)}`)
    if (resourceType) params.push(`resource_type=${encodeURIComponent(resourceType)}`)
    const query = params.length ? `?${params.join('&')}` : ''

    const { data } = await Api.get(`/api/user/purchases${query}`)
    return data
  }

  /**
   * Navigate to sign in page and redirect to specific path after successful sign in.
   * @param  {String} options.redirectUrl URL for redirect.
   */
  signIn({ redirectUrl = undefined }: SignInParams): void {
    if (redirectUrl && typeof redirectUrl !== 'string') {
      throw new Error('redirectUrl must be a string')
    }

    let url = `${HOST}/user/registration?redirect_url=`

    if (redirectUrl) url += encodeURIComponent(document.location.href)

    document.location.href = url
  }

  /**
   * Sign out user. Delete cookies and refresh page.
   */
  async signOut(reloadAfter = true): Promise<unknown> {
    const data = await Api.delete(`/user/log_out`)
    if (reloadAfter) window.location.reload()
    return data
  }

  async pushEvent(event: UserEvent): Promise<void> {
    await Api.post<void>(`/api/user/event`, {
      event: event,
    })
  }
}
